
import { defineComponent, PropType } from 'vue';
import type { TvCastWorkItem } from '@/components/timetable/tvCastTypes';

const EMPTY_WORK: TvCastWorkItem = {
  studentName: '',
  original: '',
  result: '',
};

interface SplitCanvasDrawBox {
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

interface SplitViewResizeObserver {
  disconnect(): void;
  observe(target: Element): void;
}

type SplitResizeObserverCtor = new (callback: () => void) => SplitViewResizeObserver;

function createSplitResizeObserver(callback: () => void): SplitViewResizeObserver | null {
  const win = window as typeof window & { ResizeObserver?: SplitResizeObserverCtor };
  if (!win.ResizeObserver) {
    return null;
  }
  return new win.ResizeObserver(callback);
}

interface SplitCanvasState {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  underLayer: HTMLCanvasElement;
  overLayer: HTMLCanvasElement;
  originalImg: HTMLImageElement;
  resultImg: HTMLImageElement;
  viewWidth: number;
  viewHeight: number;
  splitPct: number;
  originalSrc: string;
  resultSrc: string;
  resizeObserver: SplitViewResizeObserver | null;
}

const splitCanvasStates = new WeakMap<HTMLElement, SplitCanvasState>();

type LightboxViewMode = 'split' | 'compare';

export default defineComponent({
  name: 'TvCastModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    works: {
      type: Array as PropType<TvCastWorkItem[]>,
      default: () => [],
    },
  },
  emits: ['update:visible', 'fetch'],
  data() {
    return {
      splitMode: false,
      splitPercent: 50,
      lightboxSplitPercent: 50,
      fetchLoading: false,
      lightboxVisible: false,
      lightboxViewMode: 'split' as LightboxViewMode,
      lightboxWork: { ...EMPTY_WORK },
      peekTimer: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    validWorks(): TvCastWorkItem[] {
      return this.works.filter((item) => item.original || item.result);
    },
    hasProjected(): boolean {
      return this.validWorks.length > 0;
    },
    isBatchMode(): boolean {
      return this.validWorks.length > 1;
    },
    singleWork(): TvCastWorkItem {
      return this.validWorks[0] || EMPTY_WORK;
    },
    showSingleCompare(): boolean {
      return this.hasProjected && !this.isBatchMode && !this.splitMode;
    },
    showBatchGallery(): boolean {
      return this.hasProjected && this.isBatchMode;
    },
    showSplitCompare(): boolean {
      return this.splitMode && !this.isBatchMode;
    },
    lightboxHint(): string {
      if (this.lightboxViewMode === 'split') {
        return '左侧为原图线稿，右侧为上色效果图';
      }
      return '左侧为原图线稿，右侧为上色效果图；拖动中间竖线对照';
    },
  },
  watch: {
    visible(val: boolean) {
      if (val) {
        this.splitMode = false;
        this.splitPercent = 50;
        this.closeLightbox();
        this.$nextTick(() => {
          this.setViewSplit(this.$refs.splitViewRef as HTMLElement | undefined, 50);
          this.bindSplitDrag();
        });
      } else {
        this.exitSplitMode();
        this.clearFetchLoading();
      }
    },
    'validWorks.length'(count: number) {
      if (count > 1) {
        this.exitSplitMode();
      }
    },
  },
  beforeUnmount() {
    this.clearFetchLoading();
    this.clearPeekTimer();
    this.destroyAllSplitCanvas();
  },
  methods: {
    close() {
      this.$emit('update:visible', false);
    },
    openLightbox(item: TvCastWorkItem) {
      if (!item.original || !item.result) {
        return;
      }
      this.lightboxWork = item;
      this.lightboxVisible = true;
      this.lightboxViewMode = 'split';
      this.lightboxSplitPercent = 50;
      this.$nextTick(async () => {
        const view = this.$refs.lightboxSplitViewRef as HTMLElement | undefined;
        await this.ensureSplitCanvas(view, item.original, item.result);
        this.refreshLightboxView();
        this.attachSplitDrag(
            this.$refs.lightboxSplitBarRef as HTMLElement | undefined,
            view,
            (pct) => {
              this.lightboxSplitPercent = pct;
            },
        );
      });
    },
    closeLightbox() {
      this.lightboxVisible = false;
      this.lightboxViewMode = 'split';
      this.lightboxWork = { ...EMPTY_WORK };
      this.clearPeekTimer();
    },
    setLightboxViewMode(mode: LightboxViewMode) {
      if (this.lightboxViewMode === mode) {
        return;
      }
      this.lightboxViewMode = mode;
      this.clearPeekTimer();
      this.refreshLightboxView();
      if (mode === 'compare') {
        const view = this.$refs.lightboxSplitViewRef as HTMLElement | undefined;
        this.playSplitPeek(view, (pct) => {
          this.lightboxSplitPercent = pct;
        });
      }
    },
    isLightboxSplitView(view: HTMLElement | undefined) {
      return view === this.$refs.lightboxSplitViewRef;
    },
    refreshLightboxView() {
      const view = this.$refs.lightboxSplitViewRef as HTMLElement | undefined;
      this.refreshViewPaint(view, this.lightboxSplitPercent);
    },
    refreshViewPaint(view: HTMLElement | undefined, pct: number) {
      if (!view) {
        return;
      }
      if (this.isLightboxSplitView(view) && this.lightboxViewMode === 'split') {
        this.paintSplitSideBySide(view);
        return;
      }
      this.renderSplitView(view, pct);
    },
    paintSplitSideBySide(view: HTMLElement) {
      const state = splitCanvasStates.get(view);
      if (!state) {
        return;
      }
      const { ctx, originalImg, resultImg, viewWidth, viewHeight } = state;
      const half = viewWidth / 2;
      ctx.clearRect(0, 0, viewWidth, viewHeight);
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, half, viewHeight);
      ctx.clip();
      this.drawImageContain(ctx, originalImg, half, viewHeight);
      ctx.restore();
      ctx.save();
      ctx.translate(half, 0);
      ctx.beginPath();
      ctx.rect(0, 0, half, viewHeight);
      ctx.clip();
      this.drawImageContain(ctx, resultImg, half, viewHeight);
      ctx.restore();
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(half, 0);
      ctx.lineTo(half, viewHeight);
      ctx.stroke();
      ctx.restore();
    },
    exitSplitMode() {
      this.splitMode = false;
    },
    enterSplitMode() {
      if (!this.singleWork.original || !this.singleWork.result) {
        return;
      }
      this.splitMode = true;
      this.splitPercent = 50;
      this.$nextTick(async () => {
        const view = this.$refs.splitViewRef as HTMLElement | undefined;
        await this.ensureSplitCanvas(
            view,
            this.singleWork.original,
            this.singleWork.result,
        );
        this.setViewSplit(view, 50);
        this.playSplitPeek(view, (pct) => {
          this.splitPercent = pct;
        });
        this.bindSplitDrag();
      });
    },
    loadSplitImage(src: string) {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    },
    getImageContainBox(
        img: HTMLImageElement,
        boxWidth: number,
        boxHeight: number,
    ): SplitCanvasDrawBox {
      const imgRatio = img.naturalWidth / img.naturalHeight;
      const boxRatio = boxWidth / boxHeight;
      if (imgRatio > boxRatio) {
        const dw = boxWidth;
        const dh = boxWidth / imgRatio;
        return {
          dx: 0,
          dy: (boxHeight - dh) / 2,
          dw,
          dh,
        };
      }
      const dh = boxHeight;
      const dw = boxHeight * imgRatio;
      return {
        dx: (boxWidth - dw) / 2,
        dy: 0,
        dw,
        dh,
      };
    },
    drawImageContain(
        ctx: CanvasRenderingContext2D,
        img: HTMLImageElement,
        boxWidth: number,
        boxHeight: number,
    ) {
      const box = this.getImageContainBox(img, boxWidth, boxHeight);
      ctx.drawImage(img, box.dx, box.dy, box.dw, box.dh);
    },
    renderSplitLayer(
        layer: HTMLCanvasElement,
        ctx: CanvasRenderingContext2D,
        img: HTMLImageElement,
        width: number,
        height: number,
    ) {
      layer.width = width;
      layer.height = height;
      ctx.clearRect(0, 0, width, height);
      this.drawImageContain(ctx, img, width, height);
    },
    resizeSplitCanvas(view: HTMLElement, state: SplitCanvasState) {
      const rect = view.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      if (width === state.viewWidth && height === state.viewHeight) {
        return;
      }
      state.viewWidth = width;
      state.viewHeight = height;
      const underCtx = state.underLayer.getContext('2d');
      const overCtx = state.overLayer.getContext('2d');
      if (!underCtx || !overCtx) {
        return;
      }
      this.renderSplitLayer(
          state.underLayer,
          underCtx,
          state.resultImg,
          width,
          height,
      );
      this.renderSplitLayer(
          state.overLayer,
          overCtx,
          state.originalImg,
          width,
          height,
      );
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      state.canvas.width = Math.round(width * dpr);
      state.canvas.height = Math.round(height * dpr);
      state.canvas.style.width = `${width}px`;
      state.canvas.style.height = `${height}px`;
      state.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.refreshViewPaint(view, state.splitPct);
    },
    async ensureSplitCanvas(
        view: HTMLElement | undefined,
        original: string,
        result: string,
    ) {
      if (!view || !original || !result) {
        return;
      }
      const states = splitCanvasStates;
      const cached = states.get(view);
      if (
          cached
          && cached.originalSrc === original
          && cached.resultSrc === result
      ) {
        this.resizeSplitCanvas(view, cached);
        return;
      }
      if (cached) {
        this.destroySplitCanvas(view);
      }

      const canvas = view.querySelector('.tv-split-compare-canvas') as HTMLCanvasElement | null;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) {
        return;
      }

      const [originalImg, resultImg] = await Promise.all([
        this.loadSplitImage(original),
        this.loadSplitImage(result),
      ]);

      const underLayer = document.createElement('canvas');
      const overLayer = document.createElement('canvas');
      const underCtx = underLayer.getContext('2d');
      const overCtx = overLayer.getContext('2d');
      if (!underCtx || !overCtx) {
        return;
      }

      const state: SplitCanvasState = {
        canvas,
        ctx,
        underLayer,
        overLayer,
        originalImg,
        resultImg,
        viewWidth: 0,
        viewHeight: 0,
        splitPct: 50,
        originalSrc: original,
        resultSrc: result,
        resizeObserver: null,
      };

      const rect = view.getBoundingClientRect();
      state.viewWidth = Math.max(1, Math.round(rect.width));
      state.viewHeight = Math.max(1, Math.round(rect.height));
      this.renderSplitLayer(underLayer, underCtx, resultImg, state.viewWidth, state.viewHeight);
      this.renderSplitLayer(overLayer, overCtx, originalImg, state.viewWidth, state.viewHeight);

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(state.viewWidth * dpr);
      canvas.height = Math.round(state.viewHeight * dpr);
      canvas.style.width = `${state.viewWidth}px`;
      canvas.style.height = `${state.viewHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      state.resizeObserver = createSplitResizeObserver(() => {
        if (!view.classList.contains('is-dragging')) {
          this.resizeSplitCanvas(view, state);
        }
      });
      state.resizeObserver?.observe(view);
      states.set(view, state);
      this.refreshViewPaint(view, state.splitPct);
    },
    paintSplitCanvas(view: HTMLElement, pct: number) {
      const state = splitCanvasStates.get(view);
      if (!state) {
        return;
      }
      state.splitPct = pct;
      const splitX = (pct / 100) * state.viewWidth;
      const { ctx, underLayer, overLayer, viewWidth, viewHeight } = state;
      ctx.clearRect(0, 0, viewWidth, viewHeight);
      ctx.drawImage(underLayer, 0, 0, viewWidth, viewHeight);
      if (splitX > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, splitX, viewHeight);
        ctx.clip();
        ctx.drawImage(overLayer, 0, 0, viewWidth, viewHeight);
        ctx.restore();
      }
    },
    syncSplitBar(view: HTMLElement, pct: number) {
      const bar = view.querySelector('.tv-split-compare-bar') as HTMLElement | null;
      const state = splitCanvasStates.get(view);
      if (!bar || !state) {
        return;
      }
      const px = (pct / 100) * state.viewWidth;
      bar.style.transform = `translate3d(${px}px, 0, 0) translateX(-50%)`;
    },
    renderSplitView(view: HTMLElement | undefined, pct: number) {
      if (!view) {
        return;
      }
      this.paintSplitCanvas(view, pct);
      this.syncSplitBar(view, pct);
    },
    destroySplitCanvas(view: HTMLElement | undefined) {
      if (!view) {
        return;
      }
      const states = splitCanvasStates;
      const state = states.get(view);
      if (!state) {
        return;
      }
      state.resizeObserver?.disconnect();
      states.delete(view);
    },
    destroyAllSplitCanvas() {
      const views = [
        this.$refs.splitViewRef as HTMLElement | undefined,
        this.$refs.lightboxSplitViewRef as HTMLElement | undefined,
      ];
      views.forEach((view) => this.destroySplitCanvas(view));
    },
    setViewSplit(view: HTMLElement | undefined, pct: number) {
      this.refreshViewPaint(view, pct);
    },
    playSplitPeek(
        view: HTMLElement | undefined,
        onComplete?: (pct: number) => void,
    ) {
      if (this.isLightboxSplitView(view) && this.lightboxViewMode === 'split') {
        onComplete?.(50);
        return;
      }
      this.clearPeekTimer();
      const seq = [36, 64, 50];
      let i = 0;
      this.peekTimer = setInterval(() => {
        const pct = seq[i];
        this.setViewSplit(view, pct);
        i += 1;
        if (i >= seq.length) {
          this.clearPeekTimer();
          onComplete?.(pct);
        }
      }, 260);
    },
    clearPeekTimer() {
      if (this.peekTimer) {
        clearInterval(this.peekTimer);
        this.peekTimer = null;
      }
    },
    bindSplitDrag() {
      this.attachSplitDrag(
          this.$refs.splitBarRef as HTMLElement | undefined,
          this.$refs.splitViewRef as HTMLElement | undefined,
          (pct) => {
            this.splitPercent = pct;
          },
      );
    },
    attachSplitDrag(
        bar: HTMLElement | undefined,
        view: HTMLElement | undefined,
        onSplit: (pct: number) => void,
    ) {
      if (!bar || !view || bar.dataset.splitDragBound === '1') {
        return;
      }
      bar.dataset.splitDragBound = '1';

      let viewRect: DOMRect | null = null;
      let viewWidth = 0;
      let rafId = 0;
      let pendingClientX: number | null = null;
      let latestPct = 50;

      const pctFromClientX = (clientX: number) => {
        if (!viewRect) {
          viewRect = view.getBoundingClientRect();
          viewWidth = viewRect.width;
        }
        let pct = ((clientX - viewRect.left) / viewWidth) * 100;
        return Math.max(6, Math.min(94, pct));
      };

      const applySplit = (pct: number) => {
        latestPct = pct;
        const state = splitCanvasStates.get(view);
        const width = state?.viewWidth || viewWidth;
        const px = (pct / 100) * width;
        bar.style.transform = `translate3d(${px}px, 0, 0) translateX(-50%)`;
      };

      const flushMove = () => {
        rafId = 0;
        if (pendingClientX === null) {
          return;
        }
        const pct = pctFromClientX(pendingClientX);
        applySplit(pct);
        this.paintSplitCanvas(view, pct);
      };

      const scheduleMove = (clientX: number) => {
        pendingClientX = clientX;
        if (!rafId) {
          rafId = requestAnimationFrame(flushMove);
        }
      };

      const finishDrag = () => {
        view.classList.remove('is-dragging');
        bar.classList.remove('is-dragging');
        document.body.classList.remove('tv-split-dragging');
        viewRect = null;
        pendingClientX = null;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
        this.renderSplitView(view, latestPct);
        onSplit(latestPct);
      };

      bar.addEventListener('pointerdown', (e: PointerEvent) => {
        if (this.isLightboxSplitView(view) && this.lightboxViewMode === 'split') {
          return;
        }
        e.preventDefault();
        view.classList.add('is-dragging');
        bar.classList.add('is-dragging');
        document.body.classList.add('tv-split-dragging');
        viewRect = view.getBoundingClientRect();
        const state = splitCanvasStates.get(view);
        viewWidth = state?.viewWidth || viewRect.width;
        bar.setPointerCapture(e.pointerId);
        const pct = pctFromClientX(e.clientX);
        applySplit(pct);
        this.paintSplitCanvas(view, pct);
      });
      bar.addEventListener('pointermove', (e: PointerEvent) => {
        if (!bar.hasPointerCapture(e.pointerId)) {
          return;
        }
        scheduleMove(e.clientX);
      });
      bar.addEventListener('pointerup', (e: PointerEvent) => {
        if (bar.hasPointerCapture(e.pointerId)) {
          bar.releasePointerCapture(e.pointerId);
        }
        finishDrag();
      });
      bar.addEventListener('pointercancel', (e: PointerEvent) => {
        if (bar.hasPointerCapture(e.pointerId)) {
          bar.releasePointerCapture(e.pointerId);
        }
        finishDrag();
      });
    },
    clearFetchLoading() {
      this.fetchLoading = false;
    },
    async handleManualFetch() {
      if (this.fetchLoading) {
        return;
      }
      this.fetchLoading = true;
      try {
        await new Promise<void>((resolve) => {
          this.$emit('fetch', resolve);
        });
      } finally {
        this.fetchLoading = false;
      }
    },
  },
});
